// Minimal, dependency-free CSV parser and auto-mapping engine for subscriber import.
// Handles quoted fields, embedded commas/newlines, doubled-quote escapes,
// and auto-mapping column heuristics matching WhatChimp specs.

export function tokenizeCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === ',') {
      pushField();
      i++;
      continue;
    }
    if (c === '\r') {
      i++;
      continue;
    }
    if (c === '\n') {
      pushRow();
      i++;
      continue;
    }
    field += c;
    i++;
  }
  if (field.length > 0 || row.length > 0) pushRow();
  return rows;
}

export function parseCsv(text: string): Array<Record<string, string>> {
  const rows = tokenizeCsv(text).filter((r) => r.some((c) => c.trim() !== ''));
  if (rows.length < 1) return [];
  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1).map((r) => {
    const rec: Record<string, string> = {};
    headers.forEach((h, idx) => {
      rec[h] = (r[idx] ?? '').trim();
    });
    return rec;
  });
}

const PHONE_MATCHERS = ['phone', 'mobile', 'cell', 'whatsapp', 'number', 'phone_number', 'tele', 'telephone', 'هاتف', 'جوال'];
const NAME_MATCHERS = ['name', 'full_name', 'first_name', 'customer_name', 'subscriber', 'contact', 'الاسم', 'اسم'];
const EMAIL_MATCHERS = ['email', 'mail', 'email_address', 'البريد', 'ايميل'];
const COUNTRY_MATCHERS = ['country', 'country_code', 'بلد', 'دولة'];
const LANG_MATCHERS = ['language', 'lang', 'locale', 'لغة'];
const LIST_MATCHERS = ['list', 'lists', 'group', 'groups', 'قائمة', 'قوائم'];
const LABEL_MATCHERS = ['label', 'labels', 'tag', 'tags', 'وسم', 'تصنيف'];
const STATUS_MATCHERS = ['status', 'optin', 'opt_in', 'consent', 'الحالة'];
// Checked BEFORE status: "marketing_consent" also contains "consent".
const MARKETING_MATCHERS = ['marketing_consent', 'marketingconsent', 'marketing', 'تسويق'];

export type StandardField =
  | 'phone'
  | 'name'
  | 'email'
  | 'country'
  | 'language'
  | 'lists'
  | 'labels'
  | 'status'
  | 'marketing_consent'
  | 'skip'
  | `custom:${string}`;

export function autoMapColumnHeader(header: string, customFieldKeys: string[] = []): StandardField {
  const norm = header.trim().toLowerCase().replace(/[\s_-]+/g, '');

  if (PHONE_MATCHERS.some((m) => norm.includes(m.replace(/[\s_-]+/g, '')))) return 'phone';
  if (NAME_MATCHERS.some((m) => norm.includes(m.replace(/[\s_-]+/g, '')))) return 'name';
  if (EMAIL_MATCHERS.some((m) => norm.includes(m.replace(/[\s_-]+/g, '')))) return 'email';
  if (COUNTRY_MATCHERS.some((m) => norm.includes(m.replace(/[\s_-]+/g, '')))) return 'country';
  if (LANG_MATCHERS.some((m) => norm.includes(m.replace(/[\s_-]+/g, '')))) return 'language';
  if (LIST_MATCHERS.some((m) => norm.includes(m.replace(/[\s_-]+/g, '')))) return 'lists';
  if (LABEL_MATCHERS.some((m) => norm.includes(m.replace(/[\s_-]+/g, '')))) return 'labels';
  if (MARKETING_MATCHERS.some((m) => norm.includes(m.replace(/[\s_-]+/g, '')))) return 'marketing_consent';
  if (STATUS_MATCHERS.some((m) => norm.includes(m.replace(/[\s_-]+/g, '')))) return 'status';

  // Check custom fields
  for (const cf of customFieldKeys) {
    const cfNorm = cf.trim().toLowerCase().replace(/[\s_-]+/g, '');
    if (norm === cfNorm || norm.includes(cfNorm)) {
      return `custom:${cf}`;
    }
  }

  return 'skip';
}

export function autoMapColumns(
  headers: string[],
  customFieldKeys: string[] = [],
): Record<string, StandardField> {
  const mapping: Record<string, StandardField> = {};
  for (const h of headers) {
    mapping[h] = autoMapColumnHeader(h, customFieldKeys);
  }
  return mapping;
}
